import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";

// Suppress harmless ResizeObserver loop error (Radix UI + modal interaction)
if (typeof window !== 'undefined') {
  const origError = window.onerror;
  window.onerror = (msg, ...args) => {
    if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
    return origError ? origError(msg, ...args) : false;
  };
  window.addEventListener('error', (e) => {
    if (e.message?.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
    }
  });
}

import Home from "@/pages/Home";
import AboutUs from "@/pages/AboutUs";
import TrademarkRegistration from "@/pages/services/TrademarkRegistration";
import ClearanceSearch from "@/pages/services/ClearanceSearch";
import OwnershipTransfers from "@/pages/services/OwnershipTransfers";
import StatementOfUse from "@/pages/services/StatementOfUse";
import OfficeActions from "@/pages/services/OfficeActions";
import TrademarkMonitoring from "@/pages/services/TrademarkMonitoring";
import TTABPetition from "@/pages/services/TTABPetition";
import LLC from "@/pages/services/LLC";
import CCorporation from "@/pages/services/CCorporation";
import SCorporation from "@/pages/services/SCorporation";
import NonProfit from "@/pages/services/NonProfit";
import RegisterCopyright from "@/pages/services/RegisterCopyright";
import CopyrightEnforcement from "@/pages/services/CopyrightEnforcement";
import CopyrightTransfers from "@/pages/services/CopyrightTransfers";
import CopyrightLegal from "@/pages/services/CopyrightLegal";
import CopyrightPortfolio from "@/pages/services/CopyrightPortfolio";
import DMCACompliance from "@/pages/services/DMCACompliance";
import PatentSearch from "@/pages/services/PatentSearch";
import PatentOfficeActions from "@/pages/services/PatentOfficeActions";
import PatentApplication from "@/pages/services/PatentApplication";
import PatentLegal from "@/pages/services/PatentLegal";
import ImmigrationLaw from "@/pages/services/ImmigrationLaw";
import DueDiligence from "@/pages/services/DueDiligence";
import ITCommercialLaw from "@/pages/services/ITCommercialLaw";
import PartnershipLLP from "@/pages/services/PartnershipLLP";
import DataProtection from "@/pages/services/DataProtection";
import AmazonBrandRegistry from "@/pages/services/AmazonBrandRegistry";
import Bookkeeping from "@/pages/services/Bookkeeping";
import BusinessFormation from "@/pages/services/BusinessFormation";
import GoodStanding from "@/pages/services/GoodStanding";
import CompanyDissolution from "@/pages/services/CompanyDissolution";
import CompanyNameChange from "@/pages/services/CompanyNameChange";
import CompanyRenewal from "@/pages/services/CompanyRenewal";
import IssueShares from "@/pages/services/IssueShares";
import NomineeDirector from "@/pages/services/NomineeDirector";
import TaxAdvisory from "@/pages/services/TaxAdvisory";
import TransferShares from "@/pages/services/TransferShares";
import CFOServices from "@/pages/services/CFOServices";
import ContactUs from "@/pages/ContactUs";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import LandingPage from "@/pages/LandingPage";
import Blogs from "@/pages/media/Blogs";
import BlogPost from "@/pages/media/BlogPost";
import NewsUpdates from "@/pages/media/NewsUpdates";
import NewsArticle from "@/pages/media/NewsArticle";
import Publications from "@/pages/media/Publications";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/services/trademark-registration" element={<TrademarkRegistration />} />
          <Route path="/services/clearance-search" element={<ClearanceSearch />} />
          <Route path="/services/ownership-transfers" element={<OwnershipTransfers />} />
          <Route path="/services/statement-of-use" element={<StatementOfUse />} />
          <Route path="/services/office-actions" element={<OfficeActions />} />
          <Route path="/services/trademark-monitoring" element={<TrademarkMonitoring />} />
          <Route path="/services/ttab-petition" element={<TTABPetition />} />
          <Route path="/services/llc" element={<LLC />} />
          <Route path="/services/c-corporation" element={<CCorporation />} />
          <Route path="/services/s-corporation" element={<SCorporation />} />
          <Route path="/services/non-profit" element={<NonProfit />} />
          <Route path="/services/register-copyright" element={<RegisterCopyright />} />
          <Route path="/services/copyright-enforcement" element={<CopyrightEnforcement />} />
          <Route path="/services/copyright-transfers" element={<CopyrightTransfers />} />
          <Route path="/services/copyright-legal" element={<CopyrightLegal />} />
          <Route path="/services/copyright-portfolio" element={<CopyrightPortfolio />} />
          <Route path="/services/dmca-compliance" element={<DMCACompliance />} />
          <Route path="/services/patent-search" element={<PatentSearch />} />
          <Route path="/services/patent-office-actions" element={<PatentOfficeActions />} />
          <Route path="/services/patent-application" element={<PatentApplication />} />
          <Route path="/services/patent-legal" element={<PatentLegal />} />
          <Route path="/services/immigration-law" element={<ImmigrationLaw />} />
          <Route path="/services/due-diligence" element={<DueDiligence />} />
          <Route path="/services/it-commercial-law" element={<ITCommercialLaw />} />
          <Route path="/services/partnership-llp" element={<PartnershipLLP />} />
          <Route path="/services/data-protection" element={<DataProtection />} />
          <Route path="/services/amazon-brand-registry" element={<AmazonBrandRegistry />} />
          <Route path="/services/bookkeeping" element={<Bookkeeping />} />
          <Route path="/services/business-formation" element={<BusinessFormation />} />
          <Route path="/services/good-standing" element={<GoodStanding />} />
          <Route path="/services/company-dissolution" element={<CompanyDissolution />} />
          <Route path="/services/company-name-change" element={<CompanyNameChange />} />
          <Route path="/services/company-renewal" element={<CompanyRenewal />} />
          <Route path="/services/issue-shares" element={<IssueShares />} />
          <Route path="/services/nominee-director" element={<NomineeDirector />} />
          <Route path="/services/tax-advisory" element={<TaxAdvisory />} />
          <Route path="/services/transfer-shares" element={<TransferShares />} />
          <Route path="/services/cfo-services" element={<CFOServices />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/lp" element={<LandingPage />} />
          <Route path="/media/blogs" element={<Blogs />} />
          <Route path="/media/blogs/:slug" element={<BlogPost />} />
          <Route path="/media/news-updates" element={<NewsUpdates />} />
          <Route path="/media/news/:slug" element={<NewsArticle />} />
          <Route path="/media/publications" element={<Publications />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;